/**
 * MusicScheduleService - Web Player için Planlı Çalma Saatleri Yönetimi
 * Desktop veeq-player MusicScheduleService'inden adapt edilmiştir
 *
 * Amaç: Günlük/haftalık belirli saatlerde müziği durdur/başlat (örnek: 09:00-18:00 iş saatleri)
 *
 * Temel Fark: LevelDB yerine localStorage kullanır
 */

import { MusicSchedule, DayName } from '@/types/schedule';
import { localStateStore } from '@/utils/localStateStore';

class MusicScheduleService {
  private static instance: MusicScheduleService;

  private musicSchedule: MusicSchedule | null = null;
  private readonly STORAGE_KEY = 'musicSchedule';

  private constructor() {
    console.log('[MusicScheduleService] Initializing...');

    // localStorage'dan yükle
    this.loadMusicSchedule();
  }

  /**
   * Singleton instance
   */
  static getInstance(): MusicScheduleService {
    if (!MusicScheduleService.instance) {
      MusicScheduleService.instance = new MusicScheduleService();
    }
    return MusicScheduleService.instance;
  }

  /**
   * localStorage'dan music schedule'ı yükler
   */
  private loadMusicSchedule(): void {
    try {
      const data = localStateStore.getString(this.STORAGE_KEY);

      if (data) {
        this.musicSchedule = JSON.parse(data);
      }
    } catch (error) {
      console.error('[MusicScheduleService] Music schedule yükleme hatası:', error);
    }
  }

  /**
   * localStorage'a music schedule'ı kaydeder
   */
  private saveMusicSchedule(): void {
    try {
      if (this.musicSchedule) {
        localStateStore.setJson(this.STORAGE_KEY, this.musicSchedule);
      } else {
        localStateStore.remove(this.STORAGE_KEY);
      }
    } catch (error) {
      console.error('[MusicScheduleService] Music schedule kaydetme hatası:', error);
    }
  }

  /**
   * Planlı çalma ayarlarını set et
   * Desktop'taki setMusicSchedule() metodunun web adaptasyonu
   */
  setMusicSchedule(schedule: MusicSchedule): boolean {
    try {
      if (!schedule) {
        console.warn('[MusicScheduleService] Geçersiz schedule verisi');
        return false;
      }

      this.musicSchedule = {
        ...schedule,
        lastUpdated: new Date().toISOString()
      };

      // localStorage'a kaydet
      this.saveMusicSchedule();

      // Custom event dispatch et - Player.tsx dinleyecek
      window.dispatchEvent(new CustomEvent('music-schedule-updated', {
        detail: {
          action: 'set',
          schedule: this.musicSchedule
        }
      }));

      return true;
    } catch (error) {
      console.error('[MusicScheduleService] Music schedule set hatası:', error);
      return false;
    }
  }

  /**
   * Planlı çalma ayarlarını sil
   * Desktop'taki deleteMusicSchedule() metodunun web adaptasyonu
   */
  deleteMusicSchedule(): boolean {
    try {
      this.musicSchedule = null;

      // localStorage'dan sil
      this.saveMusicSchedule();

      // Custom event dispatch et - Player.tsx dinleyecek
      window.dispatchEvent(new CustomEvent('music-schedule-updated', {
        detail: {
          action: 'delete',
          schedule: null
        }
      }));

      return true;
    } catch (error) {
      console.error('[MusicScheduleService] Music schedule silme hatası:', error);
      return false;
    }
  }

  /**
   * Mevcut schedule bilgisini döndürür
   */
  getMusicSchedule(): MusicSchedule | null {
    return this.musicSchedule;
  }

  /**
   * Schedule'ın enabled durumunu döndürür
   */
  isEnabled(): boolean {
    return this.musicSchedule?.enabled || false;
  }

  private getDayName(date: Date): DayName {
    const dayNames: DayName[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayNames[date.getDay()];
  }

  private getPreviousDayName(date: Date): DayName {
    const previous = new Date(date);
    previous.setDate(previous.getDate() - 1);
    return this.getDayName(previous);
  }

  private getMinutesSinceMidnight(date: Date): number {
    return date.getHours() * 60 + date.getMinutes();
  }

  /**
   * Şu anki zamanın izin verilen çalma saati içinde olup olmadığını kontrol eder
   * Desktop App.js'teki checkSchedule() mantığı - Backend formatına uygun
   *
   * Backend formatı:
   * - startDateTime: ISO 8601 (örn: "2025-01-01T09:00:00.000Z")
   * - endDateTime: ISO 8601 (örn: "2025-01-01T18:00:00.000Z")
   * - days: ['monday', 'tuesday', ...] string array
   *
   * @param now - Kontrol edilecek zaman (test için parametre)
   * @returns true: Müzik çalabilir, false: Müzik durdurulmalı
   */
  isPlayingAllowed(now: Date = new Date()): boolean {
    if (!this.musicSchedule || !this.musicSchedule.enabled) {
      return true;
    }

    if (!this.musicSchedule.startDateTime || !this.musicSchedule.endDateTime) {
      console.warn('[MusicScheduleService] startDateTime veya endDateTime eksik');
      return true;
    }

    const startDate = new Date(this.musicSchedule.startDateTime);
    const endDate = new Date(this.musicSchedule.endDateTime);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      console.warn('[MusicScheduleService] Gecersiz startDateTime veya endDateTime');
      return true;
    }

    if (now < startDate || now > endDate) {
      return false;
    }

    const selectedDays = Array.isArray(this.musicSchedule.days) ? this.musicSchedule.days : [];
    if (selectedDays.length === 0) {
      console.warn('[MusicScheduleService] days bos, muzik engellenmedi');
      return true;
    }

    const currentDay = this.getDayName(now);
    const previousDay = this.getPreviousDayName(now);
    const currentMinutes = this.getMinutesSinceMidnight(now);
    const startMinutes = this.getMinutesSinceMidnight(startDate);
    const endMinutes = this.getMinutesSinceMidnight(endDate);

    if (endMinutes < startMinutes) {
      return (
        (selectedDays.includes(currentDay) && currentMinutes >= startMinutes) ||
        (selectedDays.includes(previousDay) && currentMinutes <= endMinutes)
      );
    }

    return (
      selectedDays.includes(currentDay) &&
      currentMinutes >= startMinutes &&
      currentMinutes <= endMinutes
    );
  }

  /**
   * Servisi temizle
   */
  cleanup(): void {
    this.musicSchedule = null;
  }
}

// Singleton export
export default MusicScheduleService.getInstance();
